import React from 'react';
import { useAuth } from '../hooks/useAuth';

type ActivePage = 'dashboard' | 'apiaries' | 'hives' | 'inspections' | 'alerts' | 'export' | 'admin';

interface DashboardStats {
  totalApiaries: number;
  totalHives: number;
  totalInspections: number;
  recentInspections: any[];
}

interface DashboardHomeProps {
  stats: DashboardStats;
  loadingStats: boolean;
  alerts: any[];
  alertsLoading: boolean;
  onNavigate: (page: ActivePage) => void;
}

const DashboardHome: React.FC<DashboardHomeProps> = ({ 
  stats, 
  loadingStats, 
  alerts, 
  alertsLoading,
  onNavigate 
}) => {
  const { user } = useAuth();

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Get user initials for avatar
  const getUserInitials = (name?: string) => {
    if (!name) return 'BK';
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // Get weather info (this would come from a weather service in a real implementation)
  const getWeatherInfo = () => {
    // In a real app, this would come from a weather API
    return { temperature: '18°C', condition: 'Partly Cloudy' };
  };

  const weather = getWeatherInfo();

  return (
    <div className="dashboard-home">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-header">
          <div className="welcome-content">
            <h1>Welcome back, {user?.name || 'Beekeeper'}!</h1>
            <p className="welcome-subtitle">Manage your apiaries and hive inspections with professional tools</p>
            <div className="member-info">
              <div className="info-item">
                <span className="info-icon">📍</span>
                <span>Location: {user?.location || 'Not set'}</span>
              </div>
              <div className="info-item">
                <span className="info-icon">📅</span>
                <span>Member since: {user?.joined ? formatDate(user.joined) : 'Unknown'}</span>
              </div>
              <div className="info-item">
                <span className="info-icon">🌤️</span>
                <span>Today: {weather.temperature}, {weather.condition}</span>
              </div>
            </div>
          </div>
          <button className="quick-add-btn" onClick={() => onNavigate('inspections')}>
            <span>+</span>
            <span>Quick Inspection</span>
          </button>
        </div>
      </section>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Apiaries Card */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Apiaries</span>
            <div className="stat-icon">📍</div>
          </div>
          <div className="stat-value">{loadingStats ? '...' : stats.totalApiaries}</div>
          <p className="stat-description">Manage your apiary locations</p>
          <button className="stat-action" onClick={() => onNavigate('apiaries')}>
            <span>📍</span>
            <span>Manage Apiaries</span>
          </button>
        </div>

        {/* Hives Card */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Hives</span>
            <div className="stat-icon">🏠</div>
          </div>
          <div className="stat-value">{loadingStats ? '...' : stats.totalHives}</div>
          <p className="stat-description">Track all your hives</p>
          <button className="stat-action" onClick={() => onNavigate('hives')}>
            <span>🏠</span>
            <span>Manage Hives</span>
          </button>
        </div>

        {/* Inspections Card */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Recent Inspections</span>
            <div className="stat-icon">📋</div>
          </div>
          <div className="stat-value">{loadingStats ? '...' : stats.recentInspections.length}</div>
          <p className="stat-description">Latest hive checks</p>
          <button className="stat-action" onClick={() => onNavigate('inspections')}>
            <span>📋</span>
            <span>Log Inspection</span>
          </button>
        </div>

        {/* Alerts Card */}
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Active Alerts</span>
            <div className="stat-icon">⚠️</div>
          </div>
          <div className="stat-value">{alertsLoading ? '...' : alerts.length}</div>
          <p className="stat-description">Beebase notifications</p>
          <button className="stat-action" onClick={() => onNavigate('alerts')}>
            <span>⚠️</span>
            <span>View Alerts</span>
          </button>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="activity-section">
        {/* Recent Inspections */}
        <div className="activity-card">
          <div className="activity-header">
            <h2 className="activity-title">Recent Inspections</h2>
            <button className="view-all-btn" onClick={() => onNavigate('inspections')}>
              <span>View all</span>
              <span>→</span>
            </button>
          </div>
          <div className="inspection-list">
            {stats.recentInspections.length > 0 ? (
              stats.recentInspections.map((inspection: any, index: number) => (
                <div 
                  key={index} 
                  className="inspection-item"
                  onClick={() => onNavigate('inspections')}
                >
                  <div className="inspection-details">
                    <div className="inspection-icon">📋</div>
                    <div className="inspection-info">
                      <span className="inspection-label">
                        {inspection.hiveName} ({inspection.apiaryName})
                      </span>
                      <span className="inspection-date">{formatDate(inspection.date)}</span>
                    </div>
                  </div>
                  <button 
                    className="view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to inspections page with the specific inspection highlighted
                      onNavigate('inspections');
                    }}
                  >
                    View
                  </button>
                </div>
              ))
            ) : (
              <p className="no-activity">No recent inspections. Start by logging your first inspection!</p>
            )}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="activity-card">
          <div className="activity-header">
            <h2 className="activity-title">Recent Alerts</h2>
            <button className="view-all-btn" onClick={() => onNavigate('alerts')}>
              <span>View all</span>
              <span>→</span>
            </button>
          </div>
          <div className="alerts-list">
            {alerts.length > 0 ? (
              alerts.slice(0, 4).map((alert: any, index: number) => (
                <div 
                  key={index} 
                  className="alert-item"
                  onClick={() => onNavigate('alerts')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="alert-details">
                    <div className="alert-icon">⚠️</div>
                    <div className="alert-info">
                      <span className="alert-label">
                        {alert.disease || 'Disease Alert'} - {alert.region || 'Unknown Region'}
                      </span>
                      <span className="alert-date">
                        {alert.location ? alert.location : formatDate(alert.date || alert.fetchedAt)}
                      </span>
                    </div>
                  </div>
                  <button 
                    className="view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate('alerts');
                    }}
                  >
                    View
                  </button>
                </div>
              ))
            ) : (
              <p className="no-activity">No active alerts. You're all caught up!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;